import './ColumnsModal.scss';

import classNames from 'classnames';
import { COPY_SPECIFIC } from '@sonar-software/sonar-ts-constants';
import { SonarModal } from '@ui/modals';
import { SonarText } from '@ui/text';
import { UpdateQueryParams } from '@hooks';

import { Button, ModalProps } from 'antd';
import { DragDropContext, Draggable, Droppable, DropResult } from 'react-beautiful-dnd';
import { EyeFilled, EyeInvisibleFilled, LockFilled } from '@ant-design/icons';
import { getColumnsString, parseColumnString } from '@ui/table-modifier/@arch/lib';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

export type ColumnInfo = {
	id: string;
	name: string;
};

export type ColumnsQueryParams = {
	columns?: string;
};

export type ColumnsModalProps = ModalProps & {
	columns: ColumnInfo[];
	columnsString?: string;
	onCancel: () => void;
	onChange: UpdateQueryParams<ColumnsQueryParams>;
};

export const ColumnsModal: React.FC<ColumnsModalProps> = ({
	columns,
	columnsString,
	onCancel,
	onChange,
	...rest
}) => {
	const [ dropDestination, setDropDestination ] = useState(-1);
	const [ columnIds, setColumnIds ] = useState<string[]>([]);
	const [ visibleColumns, setVisibleColumns ] = useState<Record<string, boolean>>({});

	const originalColumnIdsRef = useRef<string[]>([]);
	const originalColumnVisibilityRef = useRef({});

	useEffect(() => {
		const { ids, isVisible } = parseColumnString(
			columnsString ? columnsString : getColumnsString(columns.map((column) => column.id))
		);

		originalColumnIdsRef.current = ids;
		originalColumnVisibilityRef.current = isVisible;

		setColumnIds(ids);
		setVisibleColumns({ ...isVisible });
	}, [ columns, columnsString ]);

	const columnNames = useMemo(
		() =>
			columns.reduce((names, column) => {
				names[ column.id ] = column.name;

				return names;
			}, {} as Record<string, string>),
		[ columns ]
	);

	const handleHideColumn = useCallback(
		(id: string) => {
			setVisibleColumns((prevState) => ({
				...prevState,
				[ id ]: !prevState[ id ]
			}));
		},
		[ setVisibleColumns ]
	);

	const handleOnCancel = useCallback(() => {
		setColumnIds(originalColumnIdsRef.current);
		setVisibleColumns(originalColumnVisibilityRef.current);
		onCancel();
	}, [ onCancel ]);

	const handleOnUpdate = useCallback(() => {
		const columnsStr = getColumnsString(columnIds, visibleColumns);
		onChange({ columns: columnsStr });
		onCancel();
	}, [ columnIds, onCancel, onChange, visibleColumns ]);

	const handleDragChange = useCallback((dropResult: DropResult) => {
		const source = dropResult.source.index;
		const destination = dropResult.destination?.index ?? -1;
		const index = destination < source ? destination : destination + 1;
		setDropDestination(index);
	}, []);

	const handleReorderColumns = useCallback((dropResult: DropResult) => {
		setDropDestination(-1);
		const { source, destination } = dropResult;

		if (!destination) {
			return;
		}

		setColumnIds((arr) => {
			const entry = arr[ source.index ];
			const newOrder = [ ...arr.slice(0, source.index), ...arr.slice(source.index + 1) ];
			newOrder.splice(destination.index, 0, entry);

			return newOrder;
		});
	}, []);

	return (
		<SonarModal
			centered
			className='data-dictionary-columns-modal'
			footer={
				<>
					<Button onClick={ handleOnCancel }>{ COPY_SPECIFIC.CANCEL }</Button>
					<Button
						onClick={ handleOnUpdate }
						type='primary'>
						{ COPY_SPECIFIC.UPDATE }
					</Button>
				</>
			}
			maskClosable={ false }
			onCancel={ handleOnCancel }
			title={ COPY_SPECIFIC.COLUMNS }
			width={ 300 }
			{ ...rest }>
			<DragDropContext
				onDragEnd={ handleReorderColumns }
				onDragStart={ handleDragChange }
				onDragUpdate={ handleDragChange }>
				<Droppable droppableId='columns'>
					{ (provided) =>
						<div
							ref={ provided.innerRef }
							{ ...provided.droppableProps }>
							{ columnIds.map((columnId, index) => {
								if (index === 0) {
									return (
										<div
											className='column-card'
											key={ columnId }>
											<SonarText>{ columnNames[ columnId ] }</SonarText>
											<LockFilled />
										</div>
									);
								}

								return (
									<Draggable
										draggableId={ String(columnId) }
										index={ index }
										isDragDisabled={ index === 0 }
										key={ columnId }>
										{ (draggableProvided, dragSnapshot) =>
											<div
												className={ classNames('column-card', {
													dragging: dragSnapshot.isDragging,
													drop: dropDestination === index,
													hidden: !visibleColumns[ columnId ]
												}) }
												ref={ draggableProvided.innerRef }
												{ ...draggableProvided.draggableProps }
												{ ...draggableProvided.dragHandleProps }>
												<SonarText>{ columnNames[ columnId ] }</SonarText>
												<div
													className='hide-column-toggle'
													onClick={ handleHideColumn.bind(null, columnId) }>
													{ visibleColumns[ columnId ] ?
														<EyeFilled /> :
														<EyeInvisibleFilled /> }
												</div>
											</div> }
									</Draggable>
								);
							}) }
							{ provided.placeholder && <div className='drag-placeholder'>{ provided.placeholder }</div> }
						</div> }
				</Droppable>
			</DragDropContext>
		</SonarModal>
	);
};
